import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

/**
 * Which Amazon: a menu of our own rather than the browser's (Huanrui,
 * 2026-10-02). The browser draws a <select>'s list itself -- a grey box with
 * a blue highlight that matches nothing on the page -- and it cannot be
 * styled. This one is drawn like the bar it sits in: dark glass in the middle
 * of the empty page, white once the bar has moved up (amazon.css).
 *
 * Keyboard as a list box: Enter, Space or the arrows open it; the arrows move,
 * Enter picks, Escape or Tab closes. A click anywhere else closes it.
 */
/**
 * A choice drawn like the bar it sits in (2026-10-14): what the Amazon
 * picker was, with the list handed in, so the External Library's source menu
 * is the same menu as Which Amazon (MarketPicker.tsx is now this, with the
 * markets). Why it is ours rather than a <select> is said below.
 */
export interface PillOption<T extends string> {
  value: T
  label: string
}

export function PillPicker<T extends string>({
  value,
  options,
  onChange,
  short = false,
  name,
  align = 'right',
  round = false,
}: {
  value: T
  options: PillOption<T>[]
  onChange: (next: T) => void
  /** The button shows the value's two letters rather than its label. */
  short?: boolean
  /** What is being chosen, for screen readers: "Which Amazon". */
  name: string
  /** Which edge the menu lines up with: the right for a picker at the end of a bar, the left at its start. */
  align?: 'left' | 'right'
  /** A round button showing the choice's first letter (External Library: A / G / U). */
  round?: boolean
}) {
  const MARKETS = options
  const marketOf = (one: T) => options.find((option) => option.value === one) ?? options[0]
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(() => Math.max(0, MARKETS.findIndex((one) => one.value === value)))
  const root = useRef<HTMLDivElement | null>(null)
  const menu = useRef<HTMLUListElement | null>(null)
  const listId = useId()
  /**
   * The menu's height, held to the room the window has below the button
   * (2026-10-05). On a short screen -- a TV at 1080p -- the list ran off the
   * bottom at India, and the page does not scroll there; now the list
   * scrolls inside itself instead. Opens upwards when there is more room above.
   */
  const [room, setRoom] = useState<{ max: number; up: boolean } | null>(null)

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open])

  const show = () => {
    setActive(Math.max(0, MARKETS.findIndex((one) => one.value === value)))
    const box = root.current?.getBoundingClientRect()
    if (box) {
      const below = window.innerHeight - box.bottom - 24
      const above = box.top - 24
      const up = below < 200 && above > below
      setRoom({ max: Math.max(120, up ? above : below), up })
    }
    setOpen(true)
  }

  // The option the arrows are on stays in sight when the list scrolls.
  useLayoutEffect(() => {
    if (!open) return
    const option = menu.current?.children[active] as HTMLElement | undefined
    option?.scrollIntoView({ block: 'nearest' })
  }, [open, active])
  const pick = (next: T) => {
    onChange(next)
    setOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault()
        show()
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Tab') {
      setOpen(false)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((at) => Math.min(MARKETS.length - 1, at + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((at) => Math.max(0, at - 1))
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      pick(MARKETS[active].value)
    }
  }

  return (
    <div className={`amz-pick${open ? ' amz-pick--open' : ''}${align === 'left' ? ' amz-pick--left' : ''}${round ? ' amz-pick--round' : ''}`} ref={root}>
      <button
        type="button"
        className="amz-pick__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${name}: ${marketOf(value).label}`}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
      >
        <span>
          {round
            ? marketOf(value).label.slice(0, 1).toUpperCase()
            : short
              ? value.toUpperCase().slice(0, 2)
              : marketOf(value).label}
        </span>
        <svg className="amz-pick__chevron" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <ul
          ref={menu}
          className={`amz-pick__menu${room?.up ? ' amz-pick__menu--up' : ''}`}
          role="listbox"
          id={listId}
          aria-label={name}
          style={room ? ({ '--pick-room': `${Math.round(room.max)}px` } as React.CSSProperties) : undefined}
        >
          {MARKETS.map((one, index) => (
            <li
              key={one.value}
              role="option"
              aria-selected={one.value === value}
              className={`amz-pick__option${index === active ? ' amz-pick__option--active' : ''}`}
              onPointerEnter={() => setActive(index)}
              onPointerDown={(event) => event.preventDefault()} // keep the focus on the button
              onClick={() => pick(one.value)}
            >
              <span>{one.label}</span>
              {one.value === value ? (
                <svg className="amz-pick__tick" viewBox="0 0 12 10" aria-hidden="true" focusable="false">
                  <path d="M1 5.2 4.3 8.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
